import { swLog } from 'serviceWorker/helpers/makeSwLogger';
import { LogLevel } from 'shared/types/logger';

/**
 * Stores what the server saved before its change is answered, so reads that
 * follow the answer (a details view opened right after a create) find it. The
 * server's change succeeded: a failed store never rejects it (a retry would
 * repeat it), it is logged and the next sync stores the entity.
 * Resolves `true` when stored.
 */
export async function storeServerAnswer(entityName: string, store: () => Promise<unknown>): Promise<boolean> {
  try {
    await store();
    return true;
  } catch (error) {
    swLog(`Could not store the ${entityName} the server saved; the next sync stores it`, LogLevel.INFO, error, null);
    return false;
  }
}

interface ServerAnswerReads<TModel> {
  /** Stores what the server saved. */
  store: () => Promise<unknown>;
  /** The stored entity as the answer's model; `undefined` when it is not stored. */
  readStored: () => Promise<TModel | undefined>;
  /** The answer's model from the server's answer alone, without IndexedDB. */
  fromServerAnswer: () => TModel;
}

/**
 * Stores what the server saved and answers with the stored entity. The
 * server's change succeeded, so the answer never fails on IndexedDB: when the
 * store fails the database is not read again, and when the read fails too the
 * answer is built from the server's answer (logged; the next sync stores it).
 */
export async function storeAndReadServerAnswer<TModel>(
  entityName: string,
  { store, readStored, fromServerAnswer }: ServerAnswerReads<TModel>
): Promise<{ model: TModel; stored: boolean }> {
  const stored = await storeServerAnswer(entityName, store);
  if (!stored) {
    return { model: fromServerAnswer(), stored };
  }
  try {
    return { model: (await readStored()) ?? fromServerAnswer(), stored };
  } catch (error) {
    swLog(`Could not read the ${entityName} just stored; answering with the server's`, LogLevel.INFO, error, null);
    return { model: fromServerAnswer(), stored };
  }
}
